import * as React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { ChevronRight, MessageCircleQuestion, MessagesSquare } from "lucide-react";

import { FOCUS_RING } from "./lib/focus";
import { deeper, headingTag, HeadingLevelProvider, useHeadingLevel, type HeadingLevel, type HeadingTag } from "./lib/heading";
import { cn } from "./lib/utils";
import { Card, CardContent } from "./ui/card";
import { Icon, type IconComponent } from "./ui/icon";

/**
 * v2 CustomerRequestCard, ConversationSummaryCard, GeneralNotesSection
 * (redesign phase 5, round 10, #455): the cards of the source review
 * workspace (break #145). Presentational: v1 `SourceReview` keeps the
 * request list's seed logic, the per-card error boundary and the facts
 * notebook, and renders these (04, "Source review").
 *
 * All three are compact Cards (radius 12, 1px border, flat); v1 had
 * `rounded-2xl` with navy shadows that lifted on hover.
 *
 * Collapsible header: a band like CardHeader compact (padding 8 / 12). The
 * trigger holds the leading chevron (the Disclosure language: right, turns
 * 90°), the 16 icon and the title (Sora 14 Semibold, wraps); `meta` sits
 * beside the trigger, not inside it, because it may be interactive (a
 * ConfidenceBadge is a button). The heading wraps the trigger. The body is
 * under a `--divider` line; collapsed, it is unmounted.
 */

interface CollapsibleCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode;
  icon: IconComponent;
  meta?: React.ReactNode;
  heading: HeadingTag;
  /** False: a static header, the body always shown. */
  collapsible: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  bodyClassName?: string;
}

function CollapsibleCard({
  title,
  icon,
  meta,
  heading: Heading,
  collapsible,
  open,
  defaultOpen = true,
  onOpenChange,
  bodyClassName,
  children,
  ...props
}: CollapsibleCardProps) {
  const header = (trigger: React.ReactNode) => (
    <div className="flex min-h-10 items-start gap-2 px-3 py-2">
      <Heading className="flex min-w-0 flex-1 font-sans text-label text-ink">{trigger}</Heading>
      {meta != null && <span className="flex h-5 shrink-0 items-center">{meta}</span>}
    </div>
  );
  const titleParts = (
    <>
      <span className="flex h-5 shrink-0 items-center text-ink-muted">
        <Icon icon={icon} />
      </span>
      <span className="min-w-0 wrap-break-word">{title}</span>
    </>
  );
  const body = <div className={cn("flex flex-col gap-2 border-t border-divider p-3", bodyClassName)}>{children}</div>;

  if (!collapsible) {
    return (
      <Card density="compact" {...props}>
        {header(<span className="flex min-w-0 flex-1 items-start gap-2">{titleParts}</span>)}
        {body}
      </Card>
    );
  }

  return (
    <Card density="compact" {...props}>
      <CollapsiblePrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        {header(
          <CollapsiblePrimitive.Trigger
            className={cn(
              "group flex min-w-0 flex-1 cursor-pointer items-start gap-2 rounded-sm text-left outline-hidden",
              FOCUS_RING,
            )}
          >
            <span className="flex h-5 shrink-0 items-center">
              <Icon
                icon={ChevronRight}
                className="text-ink-muted transition-transform duration-150 group-hover:text-ink group-data-[state=open]:rotate-90 motion-reduce:transition-none"
              />
            </span>
            {titleParts}
          </CollapsiblePrimitive.Trigger>,
        )}
        <CollapsiblePrimitive.Content>{body}</CollapsiblePrimitive.Content>
      </CollapsiblePrimitive.Root>
    </Card>
  );
}

export interface CustomerRequestCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** The customer's request, as extracted. */
  title: string;
  /** Default `MessageCircleQuestion`. */
  icon?: IconComponent;
  /** Beside the title: the groundedness (`ConfidenceBadge size="xs"`, or a Badge). */
  meta?: React.ReactNode;
  /** Default: the context level (h3 in a pane, h3 on its own). */
  headingLevel?: HeadingLevel;
  open?: boolean;
  /** Default open, as v1. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Default `true`: the header folds the body away. `false` keeps the body always shown under a static
   * header with no chevron, for a surface where the request's facts are the working area and folding
   * them away has no use (the agent page's Kundenanliegen).
   */
  collapsible?: boolean;
  /** Extra classes for the body, e.g. `border-t-0 pt-0` to drop the divider under the title. */
  bodyClassName?: string;
  /** The body: the request's facts and its "Quellen" disclosure. None → a static header only. */
  children?: React.ReactNode;
}

export function CustomerRequestCard({
  title,
  icon = MessageCircleQuestion,
  meta,
  headingLevel,
  collapsible = true,
  bodyClassName,
  children,
  ...props
}: CustomerRequestCardProps) {
  const heading = headingTag(useHeadingLevel(headingLevel, 3));
  if (children == null) {
    // A bodyless request carries no control (v1).
    return (
      <Card density="compact" {...props}>
        <div className="flex min-h-10 items-start gap-2 px-3 py-2">
          {React.createElement(
            heading,
            { className: "flex min-w-0 flex-1 items-start gap-2 font-sans text-label text-ink" },
            <span className="flex h-5 shrink-0 items-center text-ink-muted">
              <Icon icon={icon} />
            </span>,
            <span className="min-w-0 wrap-break-word">{title}</span>,
          )}
          {meta != null && <span className="flex h-5 shrink-0 items-center">{meta}</span>}
        </div>
      </Card>
    );
  }
  return (
    <CollapsibleCard
      title={title}
      icon={icon}
      meta={meta}
      heading={heading}
      collapsible={collapsible}
      bodyClassName={bodyClassName}
      {...props}
    >
      {children}
    </CollapsibleCard>
  );
}

export interface ConversationSummaryCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** The rolling whole-thread summary; empty → nothing renders. */
  summary: string | null | undefined;
  /** Default: the context level (h3 in a pane, h3 on its own). */
  headingLevel?: HeadingLevel;
  /** Owned by the caller (persisted per workspace). Without `onOpenChange` the card is static. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  labels: { title: string };
}

export function ConversationSummaryCard({
  summary,
  headingLevel,
  open,
  onOpenChange,
  labels,
  ...props
}: ConversationSummaryCardProps) {
  const heading = headingTag(useHeadingLevel(headingLevel, 3));
  if (!summary?.trim()) return null;
  return (
    <CollapsibleCard
      title={labels.title}
      icon={MessagesSquare}
      heading={heading}
      collapsible={!!onOpenChange}
      open={onOpenChange ? (open ?? true) : undefined}
      onOpenChange={onOpenChange}
      {...props}
    >
      <p className="font-body text-body-long text-ink whitespace-pre-line wrap-break-word">{summary.trim()}</p>
    </CollapsibleCard>
  );
}

export interface GeneralNotesSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** "Allgemeine Notizen" */
  title: string;
  /** Default: the context level (h3 in a pane, h3 on its own). */
  headingLevel?: HeadingLevel;
  /** The ticket-level facts and the sources that belong to no request. */
  children: React.ReactNode;
}

/** The ticket-level section: a heading above a compact Card (v1 `GeneralNotesSection` + `GeneralSectionCard`). */
export function GeneralNotesSection({ title, headingLevel, children, className, ...props }: GeneralNotesSectionProps) {
  const id = React.useId();
  const level = useHeadingLevel(headingLevel, 3);
  const Heading = headingTag(level);
  return (
    <section aria-labelledby={id} className={cn("flex flex-col gap-2", className)} {...props}>
      <Heading id={id} className="font-sans text-label text-ink">
        {title}
      </Heading>
      <HeadingLevelProvider level={deeper(level)}>
        <Card density="compact">
          <CardContent>{children}</CardContent>
        </Card>
      </HeadingLevelProvider>
    </section>
  );
}
